import React from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { ROLE_LABELS, PLAN_LABELS } from "@/components/shared/PlanGuard";
import {
  LayoutDashboard, ChevronDown, LogOut, Menu, X, Bell,
  Building2, ChevronRight, User, Database, CloudUpload, Shield,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import hosys from "@/api/api";
import useSWUpdateAvailable from "@/hooks/useSWUpdateAvailable";
import useOfflineQueueCount from "@/hooks/useOfflineQueueCount";
import { activateWaitingWorker } from "@/swRegistration";

export function TopHeader({ user, userRole, currentVenue, venues, switchVenue, alertCount, mobileMenuOpen, setMobileMenuOpen, onProfileOpen }) {
  const updateAvailable = useSWUpdateAvailable();
  const { count: pendingSync, online: connOnline } = useOfflineQueueCount();

  const handleLogoClick = (e) => {
    if (updateAvailable) {
      e.preventDefault();
      activateWaitingWorker();
    }
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-screen-xl mx-auto px-4 h-[60px] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            className="lg:hidden p-2 -ml-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          <Link
            to={createPageUrl("Dashboard")}
            className="flex items-center gap-2.5"
            onClick={handleLogoClick}
            title={updateAvailable ? "Nova versió disponible — fes clic per actualitzar" : "Ho-Sys"}
            data-testid="hosys-logo-link"
          >
            <div className="relative w-8 h-8 rounded-xl overflow-hidden bg-white flex items-center justify-center shadow-sm border border-gray-200">
              <img src="/logo192.png" alt="Ho-Sys" className="w-full h-full object-contain" />
              {updateAvailable && (
                <span
                  className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-orange-500 rounded-full ring-2 ring-white animate-pulse"
                  data-testid="sw-update-dot"
                />
              )}
            </div>
            <span className="font-bold text-gray-900 text-base tracking-tight">Ho-Sys</span>
          </Link>

          {venues.length > 0 && <div className="hidden sm:flex items-center gap-1 text-gray-300"><ChevronRight className="w-3.5 h-3.5" /></div>}

          <VenueSelector currentVenue={currentVenue} venues={venues} switchVenue={switchVenue} />
        </div>

        <div className="flex items-center gap-2">
          {!connOnline && (
            <span className="hidden sm:flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-lg" title="Sense connexió">
              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
              Offline
            </span>
          )}
          {pendingSync > 0 && (
            <Link to={createPageUrl("SyncStatus")}>
              <button
                className={`relative px-2 py-1.5 text-xs font-medium rounded-xl transition-colors flex items-center gap-1.5 ${connOnline ? "bg-blue-50 text-blue-700 hover:bg-blue-100" : "bg-amber-50 text-amber-700"}`}
                title={`${pendingSync} operació(ns) pendents de sincronitzar`}
                data-testid="sync-pending-badge"
              >
                <CloudUpload className="w-3.5 h-3.5" />
                {pendingSync}
              </button>
            </Link>
          )}
          {alertCount > 0 && (
            <Link to={createPageUrl("NotificationCenter")}>
              <button className="relative p-2 text-gray-700 hover:bg-red-50 hover:text-red-700 rounded-xl transition-colors" data-testid="alert-bell">
                <Bell className="w-[18px] h-[18px]" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" />
              </button>
            </Link>
          )}

          <UserDropdown user={user} userRole={userRole} onProfileOpen={onProfileOpen} />
        </div>
      </div>
    </header>
  );
}

function VenueSelector({ currentVenue, venues, switchVenue }) {
  if (venues.length > 1) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="text-white gap-1.5 font-medium h-8 px-2.5 rounded-lg border-[#7B2D3F] bg-[#7B2D3F] shadow-sm hover:bg-[#6a2636] hover:text-white">
            <Building2 className="w-3.5 h-3.5 text-white" />
            <span className="hidden sm:inline max-w-[140px] truncate text-sm text-white">{currentVenue?.name || "Selecciona local"}</span>
            <ChevronDown className="w-3 h-3 text-white" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="shadow-xl border border-gray-200 rounded-2xl p-1.5 min-w-[200px]">
          {venues.map(v => (
            <DropdownMenuItem key={v.id} onClick={() => switchVenue(v)} className="rounded-xl py-2.5 px-3 cursor-pointer font-medium text-sm text-gray-900">
              <Building2 className="w-4 h-4 mr-2 text-[#7B2D3F]" />{v.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
  if (currentVenue) {
    return (
      <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
        <Building2 className="w-3.5 h-3.5 text-gray-900" />
        <span className="hidden sm:inline">{currentVenue.name}</span>
      </span>
    );
  }
  return null;
}

function UserDropdown({ user, userRole, onProfileOpen }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 h-9 px-2.5 bg-[#7B2D3F] border border-[#7B2D3F] hover:bg-[#6a2636] rounded-xl transition-colors shadow-sm" data-testid="user-menu-trigger">
          <div className="w-8 h-8 rounded-xl bg-gradient-brand flex items-center justify-center shadow-sm flex-shrink-0">
            <span className="text-white text-xs font-bold">{user?.full_name?.charAt(0)?.toUpperCase() || "U"}</span>
          </div>
          <div className="hidden sm:block text-left">
            <p className="text-xs font-semibold text-white leading-tight">{user?.full_name?.split(" ")[0]}</p>
            <p className="text-[10px] text-white/70 font-medium leading-tight">{ROLE_LABELS[userRole] || userRole}</p>
          </div>
          <ChevronDown className="w-3 h-3 text-white/70 hidden sm:block" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="shadow-xl border border-gray-200 rounded-2xl p-1.5 min-w-[200px]">
        <div className="px-3 py-3 mb-1">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-brand flex items-center justify-center">
              <span className="text-white font-bold text-sm">{user?.full_name?.charAt(0)?.toUpperCase()}</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{user?.full_name}</p>
              <p className="text-xs text-gray-700">{user?.email}</p>
              <p className="text-[10px] text-[#7B2D3F] font-semibold mt-0.5">{ROLE_LABELS[userRole] || userRole}</p>
            </div>
          </div>
        </div>
        <DropdownMenuSeparator className="my-1" />
        <DropdownMenuItem onClick={onProfileOpen} className="rounded-xl py-2.5 px-3 cursor-pointer" data-testid="edit-profile-btn">
          <User className="w-4 h-4 mr-2" />Editar perfil
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="rounded-xl py-2.5 px-3 cursor-pointer" data-testid="sync-status-link">
          <Link to={createPageUrl("SyncStatus")}>
            <CloudUpload className="w-4 h-4 mr-2" />Estat sincronització
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="rounded-xl py-2.5 px-3 cursor-pointer" data-testid="rgpd-link">
          <Link to={createPageUrl("RGPDCenter")}>
            <Shield className="w-4 h-4 mr-2" />Privacitat i RGPD
          </Link>
        </DropdownMenuItem>
        {userRole === "superadmin" && (
          <DropdownMenuItem asChild className="rounded-xl py-2.5 px-3 cursor-pointer" data-testid="admin-backups-link">
            <Link to={createPageUrl("AdminBackups")}>
              <Database className="w-4 h-4 mr-2" />Backups
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={() => hosys.auth.logout()}
          className="rounded-xl py-2.5 px-3 cursor-pointer text-red-700 focus:text-red-700 focus:bg-red-50"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />Tancar sessió
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function DesktopSidebar({ renderSidebar, subscription }) {
  return (
    <aside className="hidden lg:flex flex-col w-56 min-h-[calc(100vh-60px)] bg-white border-r border-gray-200 py-4 px-3 sticky top-[60px] overflow-y-auto">
      {renderSidebar()}
      <div className="mt-auto pt-6 px-3 pb-2">
        <div className="p-3 rounded-xl bg-gradient-to-br from-[#7B2D3F]/5 to-[#7B2D3F]/10 border border-[#7B2D3F]/10">
          <p className="text-[10px] font-bold text-[#7B2D3F] uppercase tracking-widest">AEH Reus</p>
          <p className="text-[10px] text-gray-700 mt-0.5">
            Pla {PLAN_LABELS[subscription?.plan || "basic"]} · {subscription?.status === "active" ? "Actiu" : subscription?.status === "trial" ? "En prova" : "—"}
          </p>
        </div>
      </div>
    </aside>
  );
}

export function MobileSlideOut({ open, onClose, renderSidebar }) {
  if (!open) return null;
  return (
    <div className="lg:hidden fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute left-0 top-[60px] bottom-0 w-72 bg-white border-r border-gray-200 shadow-2xl overflow-y-auto">
        <div className="p-4">
          {renderSidebar(onClose)}
        </div>
      </div>
    </div>
  );
}

export function MobileBottomNav({ items, currentPageName, alertCount }) {
  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200 z-50 safe-area-bottom shadow-lg" data-testid="mobile-bottom-nav">
      <div className="flex items-center justify-around h-16">
        {items.map(item => {
          const isActive = currentPageName === item.page;
          return (
            <Link
              key={item.code}
              to={createPageUrl(item.page)}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all relative ${
                isActive ? "text-[#7B2D3F]" : "text-gray-400 hover:text-gray-600"
              }`}
            >
              {isActive && <span className="absolute -top-0.5 left-1/2 -translate-x-1/2 w-5 h-0.5 bg-[#7B2D3F] rounded-full" />}
              <item.icon className={`w-[22px] h-[22px] transition-transform ${isActive ? "scale-110" : ""}`} />
              <span className={`text-[10px] font-medium ${isActive ? "font-semibold" : ""}`}>{item.label.split(" ")[0]}</span>
              {item.page === "NotificationCenter" && alertCount > 0 && (
                <span className="absolute top-1 right-2 w-2 h-2 bg-red-500 rounded-full ring-1 ring-white" />
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
